import { z } from "zod";
import { OutcomeBarSchema } from "./persona.js";
/**
 * Outcome-strategy plumbing.
 *
 * Replaces the imperative `if (metric === 'X') ... else if (metric === 'Y')`
 * dispatch in the worker with a registry keyed on `outcomeBar.metric`. Each
 * persona's manifest declares the metric; a strategy module - colocated in
 * `@healthos/persona-configs/strategies` - implements `compute(ctx)` and
 * registers itself.
 *
 * Why this shape:
 *   - Engine code never branches on persona id again. Adding a metric ships
 *     a strategy module + a manifest entry; the worker handler stays
 *     untouched.
 *   - Predicates are first-class. Common gates ("is the runway sufficient",
 *     "is a profile field set") live in one place and are Zod-validated, so
 *     a manifest can declare them without writing imperative code.
 *   - The OutcomeResult shape is unified across metrics, so the SPA's
 *     OutcomeBand renders any persona uniformly.
 *
 * Predicates are evaluated BEFORE compute - if any fail, the strategy
 * returns `no-data` with the predicate's reason. This is the seam that
 * generalises GLP-1's "user started 14 days ago, 365-day window is
 * dishonest" and Sleep's "fewer than 3 nights of data is too noisy".
 */
// ---------------------------------------------------------------------------
// Predicate DSL - small composable Zod-tagged shapes.
// ---------------------------------------------------------------------------
/**
 * `runway_at_least`: the user must have been generating outcome-relevant
 * data for at least N days. For GLP-1 this is days-since-therapy-start;
 * for Sleep it's effectively "we have at least N nights logged" (separate
 * `min_samples` predicate handles the latter exactly).
 */
const RunwayAtLeastPredicate = z
    .object({
    kind: z.literal("runway_at_least"),
    minDays: z.number().int().positive(),
    /** Profile field the start-date is read from (e.g. 'glp1StartDate').
     *  When null/missing on the profile, the predicate fails-closed. */
    fromProfileField: z.string().min(1),
})
    .strict();
/**
 * `min_samples`: the strategy needs at least N rows in the ledger window
 * before the math is honest. For Sleep this is "3 nights"; for any
 * future "weekly average X" metric, the equivalent count.
 */
const MinSamplesPredicate = z
    .object({
    kind: z.literal("min_samples"),
    n: z.number().int().positive(),
})
    .strict();
/**
 * `profile_field_set`: a profile field must be present (non-null) on the
 * user's profile. Used by GLP-1 to require `glp1CadenceDays` before the
 * adherence math runs; without cadence, the denominator is undefined.
 */
const ProfileFieldSetPredicate = z
    .object({
    kind: z.literal("profile_field_set"),
    field: z.string().min(1),
})
    .strict();
export const PredicateSchema = z.discriminatedUnion("kind", [
    RunwayAtLeastPredicate,
    MinSamplesPredicate,
    ProfileFieldSetPredicate,
]);
/**
 * Evaluate one predicate against the context. Pure function - no side
 * effects, no clock reads. Failing closed when fields are missing is
 * deliberate: silence is the product, fabricated math is not.
 */
export function evaluatePredicate(pred, ctx) {
    if (pred.kind === "runway_at_least") {
        const raw = ctx.profile?.[pred.fromProfileField];
        if (typeof raw !== "string") {
            return { ok: false, reason: `${pred.fromProfileField} not set` };
        }
        const startMs = Date.parse(`${raw}T00:00:00Z`);
        if (!Number.isFinite(startMs)) {
            return { ok: false, reason: `${pred.fromProfileField} malformed` };
        }
        const days = Math.max(0, Math.floor((ctx.now.getTime() - startMs) / (24 * 60 * 60 * 1000)));
        if (days < pred.minDays) {
            return {
                ok: false,
                reason: `runway ${days}d < required ${pred.minDays}d`,
            };
        }
        return { ok: true };
    }
    if (pred.kind === "min_samples") {
        if (ctx.sampleCount < pred.n) {
            return {
                ok: false,
                reason: `${ctx.sampleCount} samples < required ${pred.n}`,
            };
        }
        return { ok: true };
    }
    // profile_field_set
    const value = ctx.profile?.[pred.field];
    if (value === undefined || value === null) {
        return { ok: false, reason: `${pred.field} not set` };
    }
    return { ok: true };
}
/**
 * Evaluate every predicate in order. Returns the FIRST failure (or `ok` if
 * all pass). First-failure semantics so the user sees the most-blocking
 * reason, not a list.
 */
export function evaluatePredicates(preds, ctx) {
    for (const p of preds) {
        const result = evaluatePredicate(p, ctx);
        if (!result.ok)
            return result;
    }
    return { ok: true };
}
// ---------------------------------------------------------------------------
// Unified OutcomeResult - every strategy returns this shape.
// ---------------------------------------------------------------------------
const OutcomeBandIdSchema = z.enum(["on_track", "falling_behind", "at_risk"]);
/**
 * The shape `/api/me/outcome` returns regardless of metric. The SPA's
 * OutcomeBand reads against this shape only. Adding a new metric does
 * NOT change the wire - only the strategy that produces these fields.
 */
export const OutcomeResultSchema = z.object({
    status: z.enum(["tracking", "no-data", "metric-not-implemented", "no-persona"]),
    personaId: z.string(),
    outcomeBar: OutcomeBarSchema,
    windowDays: z.number().int().positive(),
    /**
     * Rate against target: `actual / outcomeBar.target`. Always 0 to N where
     * 1.0 means "exactly hit the target". Above 1 means "exceeded". The
     * SPA can format this either as a percentage (adherence) or back-derive
     * the absolute value (hours = rate × 7 for sleep).
     */
    rate: z.number().min(0).optional(),
    /** Optional absolute value (hours, mg, mmHg, …) so the SPA can choose
     *  to display either form. */
    absoluteValue: z.number().optional(),
    band: z
        .object({
        id: OutcomeBandIdSchema,
        label: z.string(),
        minRate: z.number(),
        maxRate: z.number(),
    })
        .optional(),
    /** Reason populated when status is `no-data` due to a failed predicate. */
    reason: z.string().optional(),
});
// The OutcomeStrategy interface (which needs `D1Database` from
// `@cloudflare/workers-types`) lives in `@healthos/persona-configs` -
// schemas stays wire-types-only. See `packages/persona-configs/src/
// strategies/index.ts` for the runtime contract.
//# sourceMappingURL=outcomeStrategy.js.map